import { asyncRouterMap, constantRouterMap, replaceUserRoutes } from '@/router/index';
import { getToken } from '@/utils/auth';
import { measureAuthStage, sessionChangedError } from '@/utils/authFlow';

let initializingRoutes = null;

const locallyHiddenRouteNames = ['personnelManager', 'businessAnalysis', 'localPushOperations'];

function cloneRoutes(routes) {
  if (!Array.isArray(routes)) {
    return [];
  }
  return routes.map((route) => {
    const clonedRoute = { ...route };
    if (route.meta) {
      clonedRoute.meta = { ...route.meta };
    }
    if (Array.isArray(route.children)) {
      clonedRoute.children = cloneRoutes(route.children);
    }
    return clonedRoute;
  });
}

//判断是否有权限访问该菜单
function hasPermission(menus, route) {
  if (route.name) {
    if (locallyHiddenRouteNames.indexOf(route.name) !== -1) {
      return false;
    }
    let currMenu = getMenu(route.name, menus);
    if (currMenu!=null) {
      if (!route.meta) {
        route.meta = {};
      }
      const routeTitle = route.meta.title != null ? String(route.meta.title).trim() : '';
      const routeIcon = route.meta.icon != null ? String(route.meta.icon).trim() : '';
      //设置菜单的标题、图标和可见性
      if (!routeTitle && currMenu.title != null && currMenu.title !== '') {
        route.meta.title = currMenu.title;
      }
      if (!routeIcon && currMenu.icon != null && currMenu.icon !== '') {
        route.meta.icon = currMenu.icon;
      }
      if(currMenu.hidden!=null){
        route.hidden = currMenu.hidden !== 0;
      }
      if (currMenu.sort != null && currMenu.sort !== '') {
        route.sort = currMenu.sort;
      }
      return true;
    } else {
      route.sort = 0;
      if (route.hidden !== undefined && route.hidden === true) {
        route.sort=-1;
        return true;
      } else {
        return false;
      }
    }
  } else {
    return true
  }
}

//根据路由名称获取菜单
function getMenu(name, menus) {
  for (let i = 0; i < menus.length; i++) {
    let menu = menus[i];
    if (name===menu.name) {
      return menu;
    }
  }
  return null;
}

// 辅助：按 sort 字段升序排序的比较函数（保留你原有逻辑）
function compare(property) {
  return function(a, b) {
    const value1 = a[property] !== undefined && a[property] !== null && a[property] !== ''
      ? Number(a[property])
      : 999; // 无 sort 字段默认排后面，sort=0 必须保留
    const value2 = b[property] !== undefined && b[property] !== null && b[property] !== ''
      ? Number(b[property])
      : 999;
    return value1 - value2; // 升序：sort 数值越小越靠前
  };
}

// 对菜单进行排序（首页永远排第一个）
function sortRouters(accessedRouters) {
  // 1. 先处理子路由排序（保留你原有逻辑，不改动）
  for (let i = 0; i < accessedRouters.length; i++) {
    let router = accessedRouters[i];
    if (router.children && router.children.length > 0) {
      router.children.sort(compare("sort")); // 子路由仍按 sort 排序
    }
  }

  // 2. 父路由排序：首页（name: 'shouye'）置顶，其余按 sort 排序
  accessedRouters.sort((a, b) => {
    // 规则1：如果 a 是首页 → a 排在前面
    if (a.name === "shouye") return -1;
    // 规则2：如果 b 是首页 → b 排在前面（a 往后靠）
    if (b.name === "shouye") return 1;
    // 规则3：都不是首页 → 按原有 sort 字段排序
    return compare("sort")(a, b);
  });

  return accessedRouters; // 关键：返回排序后的数组（确保排序生效）
}

//降序比较函数
/* function compare(p){
  return function(m,n){
    let a = m[p];
    let b = n[p];
    return b - a;
  }
} */

const permission = {
  state: {
    routers: constantRouterMap,
    addRouters: [],
    routesToken: '',
    initializationError: ''
  },
  mutations: {
    RESET_ROUTERS: state => {
      state.routers = constantRouterMap;
      state.addRouters = [];
      state.routesToken = '';
      state.initializationError = '';
      initializingRoutes = null;
    },
    SET_ROUTES_TOKEN: (state, token) => { state.routesToken = token; },
    SET_ROUTE_ERROR: (state, message) => { state.initializationError = message; },
    SET_ROUTERS: (state, routers) => {
      state.addRouters = routers;
      state.routers = constantRouterMap.concat(routers);
    }
  },
  actions: {
    InitializeUserRoutes({ state, rootState, commit, dispatch }) {
      const token = getToken();
      const version = rootState.user.sessionVersion;
      if (!token) return Promise.reject(Object.assign(new Error('请先登录。'), { code: 401 }));
      if (state.routesToken === token) return Promise.resolve(state.addRouters);
      if (initializingRoutes && initializingRoutes.token === token && initializingRoutes.version === version) {
        return initializingRoutes.promise;
      }
      const task = { token, version };
      const assertCurrent = () => {
        if (getToken() !== token || rootState.user.sessionVersion !== version) throw sessionChangedError();
      };
      commit('SET_ROUTE_ERROR', '');
      commit('SET_TOKEN', token);
      task.promise = measureAuthStage('permissions', async () => {
        const response = await dispatch('GetInfo');
        assertCurrent();
        await dispatch('GenerateRoutes', response.data);
        assertCurrent();
        replaceUserRoutes(state.addRouters);
        commit('SET_ROUTES_TOKEN', token);
        return state.addRouters;
      }).then(routes => {
        if (initializingRoutes === task) initializingRoutes = null;
        return routes;
      }, error => {
        if (initializingRoutes === task) initializingRoutes = null;
        throw error;
      });
      initializingRoutes = task;
      return task.promise;
    },
    GenerateRoutes({ commit }, data) {

      return new Promise(resolve => {
        const { menus } = data;
        const { username } = data;
        const sourceRouters = cloneRoutes(asyncRouterMap);
        const accessedRouters = sourceRouters.filter(v => {
          //admin帐号直接返回所有菜单
          // if(username==='admin') return true;
          if (hasPermission(menus, v)) {
            if (v.children && v.children.length > 0) {
              v.children = v.children.filter(child => {
                if (hasPermission(menus, child)) {
                  if(child.children && child.children.length > 0){
                    child.children = child.children.filter(child2 => {
                      if(hasPermission(menus, child2)){
                        return child2
                      }
                    })
                    if (child.meta && child.meta.requireVisibleChild && child.children.length === 0) {
                      return false;
                    }
                  }
                  return child
                }
                return false;
              });
              return v
            } else {
              return v
            }
          }
          return false;
        });
        //对菜单进行排序
        sortRouters(accessedRouters);
        commit('SET_ROUTERS', accessedRouters);
        resolve();
      })
    }
  }
};

export default permission;




// WEBPACK FOOTER //
// ./src/store/modules/permission.js